/**
 * Chat API — same-origin `/api` proxy in dev (webpack.config.js).
 * Paths mirror the platform (`docs/chat/chat.ts`) with `/api/v1` prefix; add-in send uses `/chat/add_in/...` per `docs/chat.md`.
 */

import { apiRequest } from "./client";
import type {
  AddInChatMessageResponse,
  AddInHistoryMessage,
  AddInHistorySuggestion,
  AddInSendChatMessagePayload,
  AddInChatSuggestionItem,
  ChatWordDiffOp,
  AvailableChatModelsResponse,
  ChatAvailableModel,
  ChatMessageItem,
  ChatSendResponse,
  ChatSession,
  CreateSessionPayload,
  SendMessagePayload,
  UpdateSessionPayload,
} from "../types/chat";

const CHAT_BASE = "/chat";
const AVAILABLE_MODELS_PATH = `${CHAT_BASE}/available-models`;
const CHAT_SESSIONS_PATH = `${CHAT_BASE}/sessions`;

// ---------------------------------------------------------------------------
// DTO mappers (snake_case from backend → camelCase types)
// ---------------------------------------------------------------------------

function parseModel(raw: unknown): ChatAvailableModel | null {
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === "string" ? r.id : "";
  const label = typeof r.label === "string" ? r.label : id;
  const provider = typeof r.provider === "string" ? r.provider : "";
  if (!id) {
    return null;
  }
  return { id, label, provider };
}

function availableChatModelsFromDto(raw: unknown): AvailableChatModelsResponse {
  const r = raw as Record<string, unknown>;
  const modelsRaw = r.models;
  const models = Array.isArray(modelsRaw)
    ? modelsRaw.map(parseModel).filter((m): m is ChatAvailableModel => m !== null)
    : [];
  const defaultModelRaw = r.default_model ?? r.defaultModel;
  const defaultModel = typeof defaultModelRaw === "string" ? defaultModelRaw : "";

  return { models, defaultModel };
}

function chatSessionFromDto(raw: unknown): ChatSession {
  const r = raw as Record<string, unknown>;
  return {
    id: String(r.id ?? ""),
    userId: String(r.user_id ?? r.userId ?? ""),
    orgId: r.org_id === null || r.org_id === undefined ? null : String(r.org_id),
    knowledgeBaseId:
      r.knowledge_base_id === null || r.knowledge_base_id === undefined
        ? null
        : String(r.knowledge_base_id),
    title: r.title === null || r.title === undefined ? null : String(r.title),
    createdAt: String(r.created_at ?? r.createdAt ?? ""),
    updatedAt: String(r.updated_at ?? r.updatedAt ?? ""),
  };
}

function chatMessageFromDto(raw: unknown): ChatMessageItem {
  const r = raw as Record<string, unknown>;
  const roleRaw = r.role;
  const role: "user" | "assistant" = roleRaw === "assistant" ? "assistant" : "user";
  return {
    id: String(r.id ?? ""),
    sessionId: String(r.session_id ?? r.sessionId ?? ""),
    role,
    content: String(r.content ?? ""),
    toolCalls: r.tool_calls ?? r.toolCalls ?? null,
    toolResults: r.tool_results ?? r.toolResults ?? null,
    createdAt: String(r.created_at ?? r.createdAt ?? ""),
  };
}

function chatSendResponseFromDto(raw: unknown): ChatSendResponse {
  const r = raw as Record<string, unknown>;
  return {
    userMessage: chatMessageFromDto(r.user_message ?? r.userMessage),
    assistantMessage: chatMessageFromDto(r.assistant_message ?? r.assistantMessage),
  };
}

function normalizeSessionArray(raw: unknown): ChatSession[] {
  if (Array.isArray(raw)) {
    return raw.map(chatSessionFromDto);
  }
  const r = raw as Record<string, unknown>;
  const items = r.data ?? r.items ?? r.sessions;
  if (Array.isArray(items)) {
    return items.map(chatSessionFromDto);
  }
  return [];
}

function normalizeMessageArray(raw: unknown): ChatMessageItem[] {
  if (Array.isArray(raw)) {
    return raw.map(chatMessageFromDto);
  }
  const r = raw as Record<string, unknown>;
  const items = r.data ?? r.items ?? r.messages;
  if (Array.isArray(items)) {
    return items.map(chatMessageFromDto);
  }
  return [];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** LiteLLM-capable models for the org (GET /chat/available-models). */
export async function fetchAvailableChatModels(): Promise<AvailableChatModelsResponse> {
  const raw = await apiRequest<unknown>(AVAILABLE_MODELS_PATH, { method: "GET" });
  return availableChatModelsFromDto(raw);
}

/** List chat sessions (paginated query params match platform; `per_page` for backend parity with KB list). */
export async function listChatSessions(page = 1, perPage = 50): Promise<ChatSession[]> {
  const sp = new URLSearchParams();
  sp.set("page", String(Math.max(1, page)));
  sp.set("per_page", String(Math.max(1, Math.min(100, perPage))));
  const raw = await apiRequest<unknown>(`${CHAT_SESSIONS_PATH}?${sp.toString()}`, { method: "GET" });
  return normalizeSessionArray(raw);
}

/** Create a new session (optional default KB from platform flow). */
export async function createChatSession(payload?: CreateSessionPayload): Promise<ChatSession> {
  const body =
    payload === undefined
      ? {}
      : {
          ...(payload.title !== undefined ? { title: payload.title } : {}),
          ...(payload.knowledgeBaseId !== undefined ? { knowledge_base_id: payload.knowledgeBaseId } : {}),
        };
  const raw = await apiRequest<unknown>(CHAT_SESSIONS_PATH, {
    method: "POST",
    jsonBody: body,
  });
  return chatSessionFromDto(raw);
}

/** Single session metadata. */
export async function getChatSession(sessionId: string): Promise<ChatSession> {
  const raw = await apiRequest<unknown>(`${CHAT_SESSIONS_PATH}/${encodeURIComponent(sessionId)}`, {
    method: "GET",
  });
  return chatSessionFromDto(raw);
}

export async function updateChatSession(sessionId: string, payload: UpdateSessionPayload): Promise<ChatSession> {
  const body: Record<string, unknown> = {};
  if (payload.title !== undefined) {
    body.title = payload.title;
  }
  if (payload.knowledgeBaseId !== undefined) {
    body.knowledge_base_id = payload.knowledgeBaseId;
  }
  const raw = await apiRequest<unknown>(`${CHAT_SESSIONS_PATH}/${encodeURIComponent(sessionId)}`, {
    method: "PATCH",
    jsonBody: body,
  });
  return chatSessionFromDto(raw);
}

export async function deleteChatSession(sessionId: string): Promise<void> {
  await apiRequest<void>(`${CHAT_SESSIONS_PATH}/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
  });
}

/** History for a session (platform GET /chat/sessions/:id/messages). */
export async function getChatMessages(
  sessionId: string,
  limit = 100,
  offset = 0
): Promise<ChatMessageItem[]> {
  const sp = new URLSearchParams();
  if (limit !== undefined) {
    sp.set("limit", String(limit));
  }
  if (offset !== undefined) {
    sp.set("offset", String(offset));
  }
  const q = sp.toString();
  const path = `${CHAT_SESSIONS_PATH}/${encodeURIComponent(sessionId)}/messages${q ? `?${q}` : ""}`;
  const raw = await apiRequest<unknown>(path, { method: "GET" });
  return normalizeMessageArray(raw);
}

/**
 * Platform web app send path: POST /chat/sessions/:id/messages.
 * `tag_filter` may carry KB-related filters; add-in maps selected KB ids per `docs/chat.md`.
 */
export async function sendChatMessage(sessionId: string, payload: SendMessagePayload): Promise<ChatSendResponse> {
  const body: Record<string, unknown> = {
    content: payload.content,
    ...(payload.tag_filter?.length ? { tag_filter: payload.tag_filter } : {}),
    ...(payload.model !== undefined && payload.model !== "" ? { model: payload.model } : {}),
  };
  const raw = await apiRequest<unknown>(
    `${CHAT_SESSIONS_PATH}/${encodeURIComponent(sessionId)}/messages`,
    {
      method: "POST",
      jsonBody: body,
    }
  );
  return chatSendResponseFromDto(raw);
}

/** Normalizes add-in POST JSON: `answer` + `suggestions[].text` (ignores rationale in UI). */
function addInChatMessageResponseFromDto(raw: unknown): AddInChatMessageResponse {
  const r = raw as Record<string, unknown>;
  const answer = typeof r.answer === "string" ? r.answer : "";
  const sugRaw = r.suggestions;
  const suggestions: AddInChatSuggestionItem[] = Array.isArray(sugRaw)
    ? sugRaw
        .map((item) => {
          const s = item as Record<string, unknown>;
          const wd = s.word_diff ?? s.wordDiff;
          const wordDiff: ChatWordDiffOp[] | undefined = Array.isArray(wd)
            ? wd
                .map((x) => {
                  const o = x as Record<string, unknown>;
                  const op = o.op;
                  const text = typeof o.text === "string" ? o.text : "";
                  if (op === "equal" || op === "deletion" || op === "insertion") {
                    return { op, text } as ChatWordDiffOp;
                  }
                  return null;
                })
                .filter((x): x is ChatWordDiffOp => x !== null && x.text.length >= 0)
            : undefined;
          return {
            text: typeof s.text === "string" ? s.text : "",
            ...(typeof s.rationale === "string" ? { rationale: s.rationale } : {}),
            ...(wordDiff ? { wordDiff } : {}),
          };
        })
        .filter((s): s is AddInChatSuggestionItem => s.text.length > 0)
    : [];
  return { answer, suggestions };
}

/**
 * Word add-in send path: POST /chat/add_in/sessions/:session_id/messages
 * Body: `selected_text`, `user_query`, `kb_selected` (KB file ids), `model` (snake_case JSON).
 */
export async function sendAddInChatMessage(
  sessionId: string,
  payload: AddInSendChatMessagePayload
): Promise<AddInChatMessageResponse> {
  const raw = await apiRequest<unknown>(
    `${CHAT_BASE}/add_in/sessions/${encodeURIComponent(sessionId)}/messages`,
    {
      method: "POST",
      jsonBody: {
        selected_text: payload.selected_text ?? null,
        user_query: payload.user_query,
        ...(payload.kb_selected?.length ? { kb_selected: payload.kb_selected } : {}),
        ...(payload.model !== undefined && payload.model !== "" ? { model: payload.model } : {}),
        ...(payload.contract_id !== undefined && payload.contract_id !== null && String(payload.contract_id).trim() !== ""
          ? { contract_id: String(payload.contract_id).trim() }
          : {}),
      },
    }
  );
  return addInChatMessageResponseFromDto(raw);
}

export type AddInRetryChatMessagePayload = {
  model?: string;
  kb_selected?: string[];
  contract_id?: string | null;
};

/**
 * Regenerate an assistant reply: DELETE prior answer server-side, re-run LLM for the
 * preceding user question. POST `/chat/add_in/sessions/{session_id}/messages/{message_id}/retry`.
 */
export async function retryAddInChatMessage(
  sessionId: string,
  messageId: string,
  payload?: AddInRetryChatMessagePayload
): Promise<AddInChatMessageResponse> {
  const raw = await apiRequest<unknown>(
    `${CHAT_BASE}/add_in/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/retry`,
    {
      method: "POST",
      jsonBody: {
        ...(payload?.model !== undefined && payload.model !== "" ? { model: payload.model } : {}),
        ...(payload?.kb_selected?.length ? { kb_selected: payload.kb_selected } : {}),
        ...(payload?.contract_id !== undefined &&
        payload.contract_id !== null &&
        String(payload.contract_id).trim() !== ""
          ? { contract_id: String(payload.contract_id).trim() }
          : {}),
      },
    }
  );
  return addInChatMessageResponseFromDto(raw);
}

function addInHistorySuggestionFromDto(raw: unknown): { text: string; rationale?: string; wordDiff?: ChatWordDiffOp[] } {
  const s = raw as Record<string, unknown>;
  const wd = s.word_diff ?? s.wordDiff;
  const wordDiff: ChatWordDiffOp[] | undefined = Array.isArray(wd)
    ? wd
        .map((x) => {
          const o = x as Record<string, unknown>;
          const op = o.op;
          const text = typeof o.text === "string" ? o.text : "";
          if (op === "equal" || op === "deletion" || op === "insertion") {
            return { op, text } as ChatWordDiffOp;
          }
          return null;
        })
        .filter((x): x is ChatWordDiffOp => x !== null && x.text.length >= 0)
    : undefined;
  return {
    text: typeof s.text === "string" ? s.text : "",
    ...(typeof s.rationale === "string" ? { rationale: s.rationale } : {}),
    ...(wordDiff ? { wordDiff } : {}),
  };
}

function addInHistoryMessageFromDto(raw: unknown): AddInHistoryMessage {
  const r = raw as Record<string, unknown>;
  const roleRaw = r.role;
  const role: "user" | "assistant" = roleRaw === "assistant" ? "assistant" : "user";
  const sugRaw = r.suggestions;
  const suggestions = Array.isArray(sugRaw) ? sugRaw.map(addInHistorySuggestionFromDto).filter((x) => x.text.length > 0) : [];
  return {
    id: String(r.id ?? ""),
    sessionId: String(r.session_id ?? r.sessionId ?? ""),
    role,
    selectedText: typeof r.selected_text === "string" ? r.selected_text : "",
    suggestions,
    createdAt: String(r.created_at ?? r.createdAt ?? ""),
    content: typeof r.content === "string" ? r.content : "",
    modelName: typeof r.model_name === "string" ? r.model_name : typeof r.modelName === "string" ? r.modelName : "",
  };
}

/**
 * Add-in message history: GET `/chat/add_in/sessions/{session_id}/messages?limit=&offset=`.
 * ``offset=0`` returns the **newest** ``limit`` messages (chronological order). Increase
 * ``offset`` by each page's length to load older pages when scrolling up.
 */
export async function getAddInChatMessageHistory(
  sessionId: string,
  limit = 20,
  offset = 0
): Promise<AddInHistoryMessage[]> {
  const sp = new URLSearchParams();
  sp.set("limit", String(Math.max(1, Math.min(100, limit))));
  sp.set("offset", String(Math.max(0, offset)));
  const path = `${CHAT_BASE}/add_in/sessions/${encodeURIComponent(sessionId)}/messages?${sp.toString()}`;
  const raw = await apiRequest<unknown>(path, { method: "GET" });
  if (!Array.isArray(raw)) {
    const r = raw as Record<string, unknown>;
    const items = r.data ?? r.items ?? r.messages;
    if (Array.isArray(items)) {
      return items.map(addInHistoryMessageFromDto);
    }
    return [];
  }
  return raw.map(addInHistoryMessageFromDto);
}

/**
 * Persist an edited suggestion on a stored assistant message.
 * PATCH `/chat/add_in/sessions/{session_id}/messages/{message_id}/suggestions/{index}`.
 */
export async function patchAddInChatSuggestion(
  sessionId: string,
  messageId: string,
  suggestionIndex: number,
  text: string
): Promise<AddInHistorySuggestion> {
  const raw = await apiRequest<unknown>(
    `${CHAT_BASE}/add_in/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/suggestions/${suggestionIndex}`,
    {
      method: "PATCH",
      jsonBody: { text },
    }
  );
  return addInHistorySuggestionFromDto(raw);
}
